import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { fetchFilterMeta } from '../../api/product';
import { getCatalog } from '../../api/catalog';
import './FilterSidebar.css'
export default function FilterSidebar({ category }) {
  const { sub: subSlug } = useParams();
  const [sp, setSp] = useSearchParams();

  // ----- mobile drawer (только UI, логика фильтра не меняется) -----
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setIsMobileOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isMobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMobileOpen]);

  // ----- значения из URL -----
  const minPrice   = sp.get('minPrice') || '';
  const maxPrice   = sp.get('maxPrice') || '';
  const inStock    = sp.get('inStock') === 'true';
  const discounted = sp.get('discounted') === 'true';
  const sort       = sp.get('sort') || 'new';
  const brandsSel  = (sp.getAll('brand')?.length ? sp.getAll('brand') : (sp.get('brand')?.split(',') || [])).filter(Boolean);
  const colorsSel  = (sp.getAll('color')?.length ? sp.getAll('color') : (sp.get('color')?.split(',') || [])).filter(Boolean);
  const countriesSel = (sp.getAll('country')?.length ? sp.getAll('country') : (sp.get('country')?.split(',') || [])).filter(Boolean);
  const sku        = sp.get('sku') || '';

  // локальные поиски по спискам
  const qBrand   = sp.get('qBrand')   || '';
  const qColor   = sp.get('qColor')   || '';
  const qCountry = sp.get('qCountry') || '';

  // ----- helper обновления URL -----
  const update = (patch) => {
    const next = new URLSearchParams(sp);
    Object.entries(patch).forEach(([k, v]) => {
      if (v === '' || v === false || v == null) next.delete(k);
      else if (Array.isArray(v)) {
        next.delete(k);
        v.forEach(val => next.append(k, val));
      } else {
        next.set(k, String(v));
      }
    });
    if (!('page' in patch)) next.delete('page');
    setSp(next, { replace: true });
  };

  // ----- meta: бренды/цвета/страны -----
  const [brandOptions, setBrandOptions] = useState([]);
  const [colorOptions, setColorOptions] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);
  const [loadingMeta, setLoadingMeta] = useState(false);

  useEffect(() => {
    let mounted = true;
    setLoadingMeta(true);
    (async () => {
      try {
        const data = await fetchFilterMeta({
          topCategorySlug: category || undefined,
          subcategoryId: sp.get('subId') || undefined,
          // учитываем базовый контекст (но НЕ brand/color/country и НЕ f[...]!)
          minPrice: sp.get('minPrice') || undefined,
          maxPrice: sp.get('maxPrice') || undefined,
          inStock: sp.get('inStock') || undefined,
          discounted: sp.get('discounted') || undefined,
          q: sp.get('q') || undefined,
        });
        if (!mounted) return;
        setBrandOptions((data.brands || []).slice(0, 200));
        setColorOptions((data.colors || []).slice(0, 200));
        setCountryOptions((data.countries || []).slice(0, 200));
      } finally {
        if (mounted) setLoadingMeta(false);
      }
    })();
    return () => { mounted = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, subSlug, sp]);

  // ----- features из подкатегории (meta) -----
  const [featureMeta, setFeatureMeta] = useState([]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const cats = await getCatalog();
      if (!mounted) return;
      const cat = (cats || []).find(c => c.slug === category);
      const subcats = cat?.subcategories || [];
      const subIdFromUrl = sp.get('subId');
      const sub = subcats.find(s =>
        s.slug === subSlug || String(s._id) === String(subIdFromUrl)
      ) || subcats[0];

      if (sub && !subIdFromUrl) {
        const next = new URLSearchParams(sp);
        next.set('subId', String(sub._id));
        setSp(next, { replace: true });
      }
      setFeatureMeta(sub?.features || []);
    })();
    return () => { mounted = false; };
  }, [category, subSlug, sp, setSp]);

  // локальная фильтрация списков
  const filteredBrands = useMemo(() => {
    const q = qBrand.trim().toLowerCase();
    if (!q) return brandOptions;
    return brandOptions.filter(b => b.toLowerCase().includes(q));
  }, [brandOptions, qBrand]);

  const filteredColors = useMemo(() => {
    const q = qColor.trim().toLowerCase();
    if (!q) return colorOptions;
    return colorOptions.filter(c => c.toLowerCase().includes(q));
  }, [colorOptions, qColor]);

  const filteredCountries = useMemo(() => {
    const q = qCountry.trim().toLowerCase();
    if (!q) return countryOptions;
    return countryOptions.filter(c => c.toLowerCase().includes(q));
  }, [countryOptions, qCountry]);

  return (
    <>
      <button type="button" className="filter__open-btn" onClick={() => setIsMobileOpen(true)}>
        Фильтры
      </button>

      <div
        className={`filter__overlay ${isMobileOpen ? "is-open" : ""}`}
        onClick={() => setIsMobileOpen(false)}
      />

      <aside className={`filter filter--drawer ${isMobileOpen ? "is-open" : ""}`}>
        <div className="filter__mobile-head">
          <div className="filter__mobile-title">Фильтры</div>
          <button
            type="button"
            className="filter__close-btn"
            onClick={() => setIsMobileOpen(false)}
            aria-label="Закрыть фильтры"
          >
            ✕
          </button>
        </div>

      {/* Цена */}
      <div className="filter__section">
        <div className="filter__title">Цена, ₽</div>
        <div className="filter__row-2">
          <input
            type="number"
            placeholder="от"
            value={minPrice}
            onChange={(e) => update({ minPrice: e.target.value })}
            min="0"
          />
          <input
            type="number"
            placeholder="до"
            value={maxPrice}
            onChange={(e) => update({ maxPrice: e.target.value })}
            min="0"
          />
        </div>
      </div>

      {/* Артикул */}
      <div className="filter__section">
        <div className="filter__title">Артикул</div>
        <input
          type="text"
          placeholder="например 110.000.00"
          value={sku}
          onChange={(e) => update({ sku: e.target.value })}
        />
        <div className="filter__muted">Поиск по артикулу</div>
      </div>

      {/* Бренды */}
      <div className="filter__section">
        <div className="filter__title">Бренды</div>
        <input
          type="text"
          placeholder="Поиск по брендам"
          value={qBrand}
          onChange={(e) => update({ qBrand: e.target.value })}
        />
        <div className="filter__list">
          {loadingMeta && <div className="filter__muted">Загрузка…</div>}
          {!loadingMeta && !filteredBrands.length && <div className="filter__muted">Бренды не найдены</div>}
          {filteredBrands.map(b => {
            const checked = brandsSel.includes(b);
            return (
              <label key={b} className="filter__check">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => {
                    const next = checked ? brandsSel.filter(x => x !== b) : [...brandsSel, b];
                    update({ brand: next.length ? next : null });
                  }}
                />
                <span>{b}</span>
              </label>
            );
          })}
        </div>
      </div>

      {/* Цвет */}
      <div className="filter__section">
        <div className="filter__title">Цвет</div>
        <input
          type="text"
          placeholder="Поиск по цветам"
          value={qColor}
          onChange={(e) => update({ qColor: e.target.value })}
        />
        <div className="filter__list">
          {loadingMeta && <div className="filter__muted">Загрузка…</div>}
          {!loadingMeta && !filteredColors.length && <div className="filter__muted">Цвета не найдены</div>}
          {filteredColors.map(c => {
            const checked = colorsSel.includes(c);
            return (
              <label key={c} className="filter__check">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => {
                    const next = checked ? colorsSel.filter(x => x !== c) : [...colorsSel, c];
                    update({ color: next.length ? next : null });
                  }}
                />
                <span>{c}</span>
              </label>
            );
          })}
        </div>
      </div>

      {/* Страна */}
      <div className="filter__section">
        <div className="filter__title">Страна</div>
        <input
          type="text"
          placeholder="Поиск по странам"
          value={qCountry}
          onChange={(e) => update({ qCountry: e.target.value })}
        />
        <div className="filter__list">
          {loadingMeta && <div className="filter__muted">Загрузка…</div>}
          {!loadingMeta && !filteredCountries.length && <div className="filter__muted">Страны не найдены</div>}
          {filteredCountries.map(c => {
            const checked = countriesSel.includes(c);
            return (
              <label key={c} className="filter__check">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => {
                    const next = checked ? countriesSel.filter(x => x !== c) : [...countriesSel, c];
                    update({ country: next.length ? next : null });
                  }}
                />
                <span>{c}</span>
              </label>
            );
          })}
        </div>
      </div>

      {/* Особенности (features) */}
      {featureMeta.length > 0 && (
      <div className="filter__section">
        <div className="filter__title">Особенности</div>

        {featureMeta.map(f => {
          const base = `f[${f.code}]`;

          // ENUM / STRING (с values)
          if (f.type === 'enum' || (f.type === 'string' && Array.isArray(f.values))) {
            const raw = sp.get(base) || '';
            const selected = raw ? raw.split(',').filter(Boolean) : [];
            const opts = (f.values || []).map(v => ({
              code: v.code ?? v,
              label: v.label?.ru ?? v.label ?? (v.code ?? v)
            }));
            return (
              <div key={f.code} className="filter__feature">
                <div className="filter__subtitle">{f.name?.ru || f.code}{f.unit ? `, ${f.unit}` : ''}</div>
                <div className="filter__list">
                  {opts.map(o => {
                    const checked = selected.includes(String(o.code));
                    return (
                      <label key={o.code} className="filter__check">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            const next = checked
                              ? selected.filter(x => x !== String(o.code))
                              : [...selected, String(o.code)];
                            update({ [base]: next.length ? next.join(',') : null });
                          }}
                        />
                        <span>{o.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            );
          }

          // NUMBER: диапазон
          if (f.type === 'number') {
            const minKey = `${base}.min`;
            const maxKey = `${base}.max`;
            const vMin = sp.get(minKey) || '';
            const vMax = sp.get(maxKey) || '';
            return (
              <div key={f.code} className="filter__feature">
                <div className="filter__subtitle">{f.name?.ru || f.code}{f.unit ? `, ${f.unit}` : ''}</div>
                <div className="filter__row-2">
                  <input
                    type="number"
                    placeholder="от"
                    value={vMin}
                    onChange={(e) => update({ [minKey]: e.target.value })}
                  />
                  <input
                    type="number"
                    placeholder="до"
                    value={vMax}
                    onChange={(e) => update({ [maxKey]: e.target.value })}
                  />
                </div>
              </div>
            );
          }

          // BOOL: чекбокс
          if (f.type === 'bool') {
            const v = sp.get(base) === 'true';
            return (
              <div key={f.code} className="filter__feature">
                <label className="filter__check">
                  <input
                    type="checkbox"
                    checked={v}
                    onChange={(e) => update({ [base]: e.target.checked ? 'true' : null })}
                  />
                  <span>{f.name?.ru || f.code}</span>
                </label>
              </div>
            );
          }

          return null;
        })}
      </div>
      )}

      {/* Наличие/скидки */}
      <div className="filter__section">
        <div className="filter__title">Наличие и скидки</div>
        <label className="filter__check">
          <input
            type="checkbox"
            checked={inStock}
            onChange={(e) => update({ inStock: e.target.checked || null })}
          />
          <span>Только в наличии</span>
        </label>
        <label className="filter__check">
          <input
            type="checkbox"
            checked={discounted}
            onChange={(e) => update({ discounted: e.target.checked || null })}
          />
          <span>Со скидкой</span>
        </label>
      </div>

      {/* Сортировка */}
      <div className="filter__section">
        <div className="filter__title">Сортировка</div>
        <select value={sort} onChange={(e) => update({ sort: e.target.value })}>
          <option value="new">Сначала новые</option>
          <option value="price_asc">Цена: по возрастанию</option>
          <option value="price_desc">Цена: по убыванию</option>
        </select>
      </div>

      <button
        className="filter__clear-all"
        onClick={() => {
          update({
            minPrice: '', maxPrice: '',
            inStock: null, discounted: null,
            sort: 'new',
            brand: [], qBrand: '',
            color: [], qColor: '',
            country: [], qCountry: '',
            sku: '',
            // очистим все f[...]
            ...(Array.from(sp.keys())
              .filter(k => k.startsWith('f['))
              .reduce((acc, k) => {
                acc[k] = null;
                return acc;
              }, {}))
          });
        }}
      >
        Сбросить фильтры
      </button>
    </aside>
    </>
  );
}